import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

/**
 * Preferenze di accessibilità dell'utente.
 * ---------------------------------------------------------------------------
 * Il portale dichiara esplicitamente che il pannello a11y è un insieme di
 * PREFERENZE UTENTE, non un overlay di accessibilità. La differenza è
 * sostanziale e va preservata:
 *
 *   · un overlay sovrappone un widget che riscrive il DOM a runtime e
 *     tipicamente PEGGIORA l'esperienza con gli screen reader;
 *   · le preferenze mutano i token CSS del documento nativo. Nessuna
 *     riscrittura del DOM, nessun ruolo ARIA inventato: la pagina resta
 *     quella che è, con altri valori di colore, dimensione e spaziatura.
 *
 * Ogni preferenza qui corrisponde a una classe su `<html>` gestita in
 * `src/styles/tokens.css`. Il provider non conosce i valori, solo i nomi.
 */

export type A11yPreference =
  | 'contrast' // alto contrasto (>= 7:1, AAA)
  | 'large-text' // 125%
  | 'larger-text' // 150%
  | 'dyslexic' // font e spaziatura per dislessia
  | 'text-spacing' // WCAG 1.4.12
  | 'underline-links' // WCAG 1.4.1: il colore non è l'unico mezzo
  | 'reduce-motion'; // WCAG 2.3.3

export const A11Y_PREFERENCES: readonly A11yPreference[] = [
  'contrast',
  'large-text',
  'larger-text',
  'dyslexic',
  'text-spacing',
  'underline-links',
  'reduce-motion',
];

/**
 * Preferenze che si escludono a vicenda. `large-text` e `larger-text` sono
 * due gradini della stessa scala: attivarle insieme darebbe 187%.
 */
const MUTUALLY_EXCLUSIVE: readonly (readonly A11yPreference[])[] = [
  ['large-text', 'larger-text'],
];

/**
 * I 6 profili rapidi del portale attuale.
 * Sono combinazioni predefinite, non funzioni nuove: ogni profilo accende un
 * insieme di preferenze già esistenti.
 */
export interface A11yProfile {
  readonly id: string;
  /** Chiave i18n dell'etichetta. */
  readonly labelKey: string;
  readonly preferences: readonly A11yPreference[];
}

export const A11Y_PROFILES: readonly A11yProfile[] = [
  { id: 'ipovisione', labelKey: 'a11y_profile_low_vision', preferences: ['larger-text', 'contrast', 'underline-links'] },
  { id: 'dislessia', labelKey: 'a11y_profile_dyslexia', preferences: ['dyslexic', 'text-spacing', 'large-text'] },
  { id: 'daltonismo', labelKey: 'a11y_profile_color_blind', preferences: ['contrast', 'underline-links'] },
  { id: 'motoria', labelKey: 'a11y_profile_motor', preferences: ['large-text', 'reduce-motion'] },
  { id: 'cognitiva', labelKey: 'a11y_profile_cognitive', preferences: ['reduce-motion', 'text-spacing', 'large-text'] },
  { id: 'anziani', labelKey: 'a11y_profile_senior', preferences: ['large-text', 'contrast', 'underline-links', 'reduce-motion'] },
];

const STORAGE_KEY = 'ic-a11y-preferences';

interface A11yContextValue {
  readonly active: readonly A11yPreference[];
  readonly isActive: (preference: A11yPreference) => boolean;
  readonly toggle: (preference: A11yPreference) => void;
  readonly applyProfile: (profileId: string) => void;
  readonly reset: () => void;
  readonly panelOpen: boolean;
  readonly setPanelOpen: (open: boolean) => void;
}

const A11yContext = createContext<A11yContextValue | null>(null);

function readStored(): A11yPreference[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is A11yPreference =>
      A11Y_PREFERENCES.includes(p as A11yPreference),
    );
  } catch {
    return [];
  }
}

function applyToDocument(active: readonly A11yPreference[]): void {
  const root = document.documentElement;
  for (const preference of A11Y_PREFERENCES) {
    root.classList.toggle(`a11y-${preference}`, active.includes(preference));
  }
}

export function A11yPreferencesProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<A11yPreference[]>(readStored);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    applyToDocument(active);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(active));
    } catch {
      /* le preferenze restano valide per la sessione */
    }
  }, [active]);

  /* Alt+A apre il pannello, Alt+L avvia la lettura vocale.
     Sono le scorciatoie già documentate del portale: cambiarle
     disorienterebbe chi le usa. `altKey` senza `ctrl`/`meta` per non
     collidere con le scorciatoie del browser. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      const key = event.key.toLowerCase();
      if (key === 'a') {
        event.preventDefault();
        setPanelOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggle = useCallback((preference: A11yPreference) => {
    setActive((current) => {
      if (current.includes(preference)) {
        return current.filter((p) => p !== preference);
      }
      // Spegne le preferenze mutuamente esclusive con quella accesa.
      const conflicting = MUTUALLY_EXCLUSIVE.filter((group) =>
        group.includes(preference),
      ).flat();
      return [...current.filter((p) => !conflicting.includes(p)), preference];
    });
  }, []);

  const applyProfile = useCallback((profileId: string) => {
    const profile = A11Y_PROFILES.find((p) => p.id === profileId);
    if (!profile) return;
    setActive([...profile.preferences]);
  }, []);

  const reset = useCallback(() => setActive([]), []);

  const value = useMemo<A11yContextValue>(
    () => ({
      active,
      isActive: (preference) => active.includes(preference),
      toggle,
      applyProfile,
      reset,
      panelOpen,
      setPanelOpen,
    }),
    [active, applyProfile, panelOpen, reset, toggle],
  );

  return <A11yContext.Provider value={value}>{children}</A11yContext.Provider>;
}

export function useA11yPreferences(): A11yContextValue {
  const context = useContext(A11yContext);
  if (!context) throw new Error('useA11yPreferences richiede <A11yPreferencesProvider>');
  return context;
}
